/**
 * How this repository gives three's built-in materials a uniform that a patched chunk declares, and
 * finds the value an earlier evaluation of a module installed.
 */
import * as THREE from 'three';
/**
 * The lit materials and `ShadowMaterial` merge `UniformsLib.lights` (r187 `ShaderLib.js:40`, `:65`,
 * `:93`, `:119`, `:289`, and `physical` through `standard`).
 */
const LIGHTS_MERGE = {
    marker: 'pointLightShadows',
    group: THREE.UniformsLib.lights,
};
/** Every material with fog merges `UniformsLib.fog`, and only they hold `fogDensity`. */
export const FOG_UNIFORMS_MERGE = {
    marker: 'fogDensity',
    group: THREE.UniformsLib.fog,
};
/**
 * Gives `value` to every built-in material that makes `merge`, and to its group, for every material
 * created after the call.
 */
export function installMergedUniform(merge, name, value) {
    for (const shader of Object.values(THREE.ShaderLib)) {
        if (merge.marker in shader.uniforms)
            shader.uniforms[name] = { value };
    }
    merge.group[name] = { value };
}
/** {@link installMergedUniform} for the lit materials. */
export function installLitUniform(name, value) {
    installMergedUniform(LIGHTS_MERGE, name, value);
}
/**
 * The value an earlier evaluation of a module gave `program`, as after a dev server reloads it, or
 * undefined before any install. Every program compiled since reads that value, so the caller keeps
 * writing it. The default, `standard`, makes both merges. The caller narrows the type.
 */
export function installedUniformValue(name, program = 'standard') {
    return THREE.ShaderLib[program]?.uniforms[name]?.value;
}
/**
 * The value installed under `name` when it is a `type`, as after a reload, or else `make`'s. An
 * installed buffer of another length is one an earlier build shaped otherwise, so `make` replaces it.
 */
export function installedOr(name, type, make, program = 'standard') {
    const value = installedUniformValue(name, program);
    if (!(value instanceof type))
        return make();
    if (!ArrayBuffer.isView(value))
        return value;
    const made = make();
    return lengthOf(value) === lengthOf(made) ? value : made;
}
function lengthOf(buffer) {
    return buffer.length;
}
//# sourceMappingURL=litUniform.js.map